import React from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Users, CalendarCheck, CheckCircle, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Label } from '../ui/label';

const STATUS_CONFIG = {
  DRAFT: { className: 'bg-gray-500' },
  APPROVED: { className: 'bg-green-500' },
  ARCHIVED: { className: 'bg-orange-500' },
};

const getStatusBadge = (status, t) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.DRAFT;
  return <Badge className={config.className}>{t(`holidayCalendarComponents.status.${status}`, { defaultValue: status })}</Badge>;
};

const formatDateTime = (dateString, language) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString(language, {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const CalendrierDetailInfoTab = ({ calendar, handleApproveCalendar }) => {
  const { t, i18n } = useTranslation();

  if (!calendar) return null;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("holidayCalendarComponents.detail.infoTitle")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground">{t("holidayCalendarComponents.detail.calendarName")}</Label>
              <p className="font-medium">{calendar.name}</p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">{t("holidayCalendarComponents.detail.status")}</Label>
              <div className="mt-1">{getStatusBadge(calendar.status, t)}</div>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">{t("holidayCommon.fields.year")}</Label>
              <p className="font-medium">{calendar.year}</p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">{t("holidayCommon.fields.country")}</Label>
              <p className="font-medium">{calendar.country}</p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">{t("holidayCalendarComponents.detail.calendarType")}</Label>
              <p className="font-medium">{calendar.is_template ? t("holidayCalendarComponents.detail.template") : t("holidayCalendarComponents.detail.standard")}</p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">{t("holidayCalendarComponents.detail.createdAt")}</Label>
              <p className="font-medium">{formatDateTime(calendar.created_at, i18n.language)}</p>
            </div>
          </div>
          {calendar.approved_at && (
            <div className="pt-4 border-t">
              <Label className="text-sm text-muted-foreground">{t("holidayCalendarComponents.detail.approvedAt")}</Label>
              <p className="font-medium">{formatDateTime(calendar.approved_at, i18n.language)}</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("holidayCalendarComponents.detail.statsTitle")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-4 bg-muted rounded-lg">
              <Calendar className="h-8 w-8 mx-auto mb-2" style={{ color: "#5e6ad2" }} />
              <p className="text-2xl font-bold" style={{ color: "#5e6ad2" }}>{calendar.holidays_count || 0}</p>
              <p className="text-sm text-muted-foreground">{t("holidayCalendarComponents.table.holidays")}</p>
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <Users className="h-8 w-8 mx-auto mb-2 text-green-400" />
              <p className="text-2xl font-bold text-green-400">{calendar.employees_count || 0}</p>
              <p className="text-sm text-muted-foreground">{t("holidayCalendarComponents.detail.assignedEmployees")}</p>
            </div>
            <div className="text-center p-4 bg-muted rounded-lg">
              <CalendarCheck className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-2xl font-bold text-foreground">{calendar.holidays_count || 0}</p>
              <p className="text-sm text-muted-foreground">{t("holidayCalendarComponents.detail.nonWorkingDays")}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {calendar.status === 'DRAFT' && (
        <Card className="border-border bg-orange-950/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-orange-400 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-medium text-foreground">{t("holidayCalendarComponents.detail.draftTitle")}</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {t("holidayCalendarComponents.detail.draftDesc")}
                </p>
                <Button onClick={handleApproveCalendar} className="mt-3 bg-orange-600 hover:bg-orange-700" size="sm">
                  <CheckCircle className="mr-2 h-4 w-4" />
                  {t("holidayCalendarComponents.detail.approve")}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CalendrierDetailInfoTab;
